// src/GDB/pages/MyDashboardsPage.jsx
import { useEffect, useMemo, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { apiGet } from "../api/client";
import ChartWidget from "../components/ChartWidget";
import { SkeletonCardGrid, SkeletonTable } from "../../shared/ui";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import "../styles/MyDashboardsPage.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

const BREAKPOINTS = {
  lg: 1200,
  md: 996,
  sm: 768,
  xs: 480,
  xxs: 0,
};

const COLS = {
  lg: 12,
  md: 12,
  sm: 12,
  xs: 12,
  xxs: 12,
};

const defaultLayoutItem = (id, idx) => ({
  i: String(id),
  x: (idx % 4) * 3, // 4 per row
  y: Math.floor(idx / 4) * 6,
  w: 3,
  h: 6,
});

export default function MyDashboardsPage() {
  const [dashboards, setDashboards] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [currentDash, setCurrentDash] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Current user info
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);

  // -------- Drilldown state (expand/minimize) ----------
  const [drilldownWidget, setDrilldownWidget] = useState(null);
  const [drilldownData, setDrilldownData] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState("");
  const [drilldownFilter, setDrilldownFilter] = useState(null);
  const [drilldownSearch, setDrilldownSearch] = useState("");

  const drilldownHeaders =
    drilldownData && drilldownData.length
      ? Object.keys(drilldownData[0])
      : [];

  const filteredDrilldownData = useMemo(() => {
    if (!drilldownData || !drilldownData.length) return [];

    let rows = drilldownData;

    if (drilldownFilter && Object.keys(drilldownFilter).length) {
      rows = rows.filter((row) =>
        Object.entries(drilldownFilter).every(([k, v]) =>
          String(row[k] ?? "") === String(v ?? "")
        )
      );
    }

    const q = drilldownSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((row) =>
        drilldownHeaders.some((h) =>
          String(row[h] ?? "").toLowerCase().includes(q)
        )
      );
    }

    return rows;
  }, [drilldownData, drilldownFilter, drilldownHeaders, drilldownSearch]);

  // -------- Load current user --------
  useEffect(() => {
    apiGet("/auth/me")
      .then((me) => setCurrentUser(me))
      .catch(console.error);

    apiGet("/users/")
      .then((res) => setUsers(res || []))
      .catch(console.error);
  }, []);

  // -------- load dashboards list (my → fallback all) ----------
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setMessage("");
      try {
        let list = [];
        try {
          // try /my first (owned / shared)
          list = await apiGet("/dashboards/my");
        } catch (err) {
          console.warn(
            "GET /dashboards/my failed (probably 401), falling back to /dashboards",
            err
          );
          list = await apiGet("/dashboards");
        }

        setDashboards(list || []);
        if (list && list.length && !selectedId) {
          setSelectedId(String(list[0].id));
        }
      } catch (err) {
        console.error(err);
        setMessage(err?.message || "Failed to load dashboards.");
      } finally {
        setIsLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- load selected dashboard details ----------
  useEffect(() => {
    if (!selectedId) {
      setCurrentDash(null);
      return;
    }

    const loadOne = async () => {
      setIsLoading(true);
      setMessage("");
      try {
        const dash = await apiGet(`/dashboards/${selectedId}`);
        setCurrentDash(dash);
      } catch (err) {
        console.error(err);
        const msg =
          err?.message || "Failed to load selected dashboard.";
        setMessage(msg);
        setCurrentDash(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadOne();
  }, [selectedId]);

  const widgets = currentDash?.widgets || [];

  // Determine if current user owns this dashboard
  const isOwner = useMemo(() => {
    if (!currentDash || !currentUser) return false;
    return currentDash.owner_user_id === currentUser.id;
  }, [currentDash, currentUser]);

  // Get owner display name
  const ownerDisplayName = useMemo(() => {
    if (!currentDash?.owner_user_id) return "Unknown";
    if (currentUser && currentDash.owner_user_id === currentUser.id) {
      return "You";
    }
    const owner = users.find((u) => u.id === currentDash.owner_user_id);
    return owner ? (owner.username || owner.email || `User ${owner.id}`) : `User ${currentDash.owner_user_id}`;
  }, [currentDash, currentUser, users]);

  // Categorize dashboards
  const ownedDashboards = useMemo(() => {
    if (!currentUser) return dashboards;
    return dashboards.filter((d) => d.owner_user_id === currentUser.id);
  }, [dashboards, currentUser]);

  const sharedDashboards = useMemo(() => {
    if (!currentUser) return [];
    return dashboards.filter((d) => d.owner_user_id !== currentUser.id);
  }, [dashboards, currentUser]);

  const layouts = useMemo(() => {
    const layout = widgets.map((w, idx) => ({
      i: String(w.id),
      ...(w.layout || defaultLayoutItem(w.id, idx)),
    }));

    return {
      lg: layout,
      md: layout,
      sm: layout,
      xs: layout,
      xxs: layout,
    };
  }, [widgets]);

  // ---------- Export current dashboard data ----------
  const downloadDashboardData = async () => {
    if (!currentDash || !widgets.length) {
      alert("No widgets to export.");
      return;
    }

    setIsLoading(true);
    try {
      const wb = XLSX.utils.book_new();
      const datasetCache = {};

      for (const w of widgets) {
        if (!w.dataset_id) continue;

        const dsId = w.dataset_id;
        if (!datasetCache[dsId]) {
          const res = await apiGet(`/query/dataset/${dsId}`);
          datasetCache[dsId] = res.data || [];
        }
        const rawRows = datasetCache[dsId];
        if (!rawRows.length) continue;

        let rowsForSheet = rawRows;
        const type = w.widget_type;

        if (type === "table") {
          rowsForSheet = rawRows;
        } else if (type === "kpi" || type === "gauge") {
          if (w.y_field) {
            rowsForSheet = rawRows.length
              ? [{ [w.y_field]: rawRows[0][w.y_field] }]
              : [];
          }
        } else {
          const fields = [
            w.x_field || null,
            w.y_field || null,
            w.group_field || null,
          ].filter(Boolean);
          if (fields.length) {
            rowsForSheet = rawRows.map((r) => {
              const obj = {};
              fields.forEach((f) => {
                obj[f] = r[f];
              });
              return obj;
            });
          } else {
            rowsForSheet = rawRows;
          }
        }

        if (!rowsForSheet.length) continue;

        const ws = XLSX.utils.json_to_sheet(rowsForSheet);
        const sheetName = (w.title || `Widget_${w.id}`)
          .replace(/[^a-zA-Z0-9]/g, "_")
          .slice(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      }

      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const safeName = (currentDash.name || "dashboard").replace(
        /[^a-z0-9_\-]+/gi,
        "_"
      );
      saveAs(blob, `${safeName}_data.xlsx`);
      setMessage("📊 Data exported");
    } catch (err) {
      console.error(err);
      setMessage("❌ Export failed");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Drilldown handlers (expand / minimize) ----------
  const openDrilldown = async (widget, filter) => {
    if (!widget?.dataset_id) {
      setMessage("Widget has no dataset configured.");
      return;
    }

    setDrilldownData([]);
    setDrilldownError("");
    setDrilldownFilter(null);
    setDrilldownSearch("");

    setDrilldownWidget(widget);
    setDrilldownFilter(
      filter && Object.keys(filter || {}).length ? filter : null
    );
    setDrilldownLoading(true);

    try {
      const url = `/query/dataset/${widget.dataset_id}`;
      const res = await apiGet(url);
      setDrilldownData(res.data || []);
    } catch (err) {
      console.error("Drilldown error:", err);
      setDrilldownError("Failed to load data for drilldown.");
    } finally {
      setDrilldownLoading(false);
    }
  };

  const closeDrilldown = () => {
    setDrilldownWidget(null);
    setDrilldownData([]);
    setDrilldownError("");
    setDrilldownFilter(null);
    setDrilldownSearch("");
  };

  // Get owner name for a dashboard in the list
  const getOwnerName = (dashboard) => {
    if (!dashboard?.owner_user_id) return "";
    if (currentUser && dashboard.owner_user_id === currentUser.id) return "";
    const owner = users.find((u) => u.id === dashboard.owner_user_id);
    return owner ? (owner.username || owner.email || `User ${owner.id}`) : `User ${dashboard.owner_user_id}`;
  };

  return (
    <div className="mydash-page">
      {/* header */}
      <div className="mydash-header">
        <div className="mydash-title-block">
          <h1 className="mydash-title">My Dashboards</h1>
          <p className="mydash-subtitle">
            View dashboards owned by you or shared with you.
          </p>
        </div>

        <div className="mydash-header-actions">
          <select
            className="mydash-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={isLoading || !dashboards.length}
            style={{ minWidth: "280px" }}
          >
            {dashboards.length === 0 && (
              <option value="">No dashboards available</option>
            )}
            
            {/* Owned dashboards section */}
            {ownedDashboards.length > 0 && (
              <optgroup label="📁 My Dashboards">
                {ownedDashboards.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </optgroup>
            )}
            
            {/* Shared dashboards section */}
            {sharedDashboards.length > 0 && (
              <optgroup label="👥 Shared with Me">
                {sharedDashboards.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} (by {getOwnerName(d)})
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          {/* Export data button */}
          <button
            type="button"
            className="btn btn--secondary btn--xs"
            onClick={downloadDashboardData}
            disabled={isLoading || !currentDash || !widgets.length}
            title="Export dashboard data"
            style={{ marginLeft: "8px" }}
          >
            📥
          </button>
        </div>
      </div>

      {/* card */}
      <div className="mydash-card">
        {message && (
          <div
            className={`mydash-message ${
              message.toLowerCase().includes("error") ||
              message.toLowerCase().includes("could not")
                ? "mydash-message--error"
                : "mydash-message--info"
            }`}
          >
            {message}
          </div>
        )}

        {isLoading && (
          <div className="mydash-loading">
            <SkeletonCardGrid cards={6} className="w-full" />
          </div>
        )}

        {!isLoading && !currentDash && (
          <div className="mydash-empty">
            <div className="mydash-empty-icon">📊</div>
            <p>Select a dashboard from the dropdown to view it.</p>
          </div>
        )}

        {!isLoading && currentDash && (
          <div className="mydash-viewer">
            <div className="mydash-viewer-header">
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <h2 className="mydash-viewer-title" style={{ margin: 0 }}>
                  {currentDash.name}
                </h2>
                
                {/* Ownership badge */}
                {isOwner ? (
                  <span
                    style={{
                      padding: "4px 10px",
                      backgroundColor: "#dcfce7",
                      color: "#166534",
                      borderRadius: "12px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                    }}
                  >
                    👤 Owner
                  </span>
                ) : (
                  <span
                    style={{
                      padding: "4px 10px",
                      backgroundColor: "#fef3c7",
                      color: "#92400e",
                      borderRadius: "12px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                    }}
                  >
                    👥 Shared by {ownerDisplayName}
                  </span>
                )}
              </div>
              
              {currentDash.description && (
                <p className="mydash-viewer-desc" style={{ marginTop: "8px" }}>
                  {currentDash.description}
                </p>
              )}
            </div>

            <div className="mydash-grid-wrapper">
              {widgets.length === 0 ? (
                <div className="mydash-empty">
                  <div className="mydash-empty-icon">🧩</div>
                  <p>This dashboard has no widgets configured yet.</p>
                </div>
              ) : (
                <ResponsiveGridLayout
                  className="mydash-grid"
                  layouts={layouts}
                  breakpoints={BREAKPOINTS}
                  cols={COLS}
                  rowHeight={30}
                  isDraggable={false}
                  isResizable={false}
                  margin={[8, 8]}
                  draggableCancel=".widget-remove-btn"
                >
                  {widgets.map((w, idx) => (
                    <div
                      key={w.id}
                      data-grid={
                        layouts.lg.find(
                          (l) => l.i === String(w.id)
                        ) || defaultLayoutItem(w.id, idx)
                      }
                      className="mydash-grid-item"
                    >
                      <ChartWidget
                        widget={w}
                        compact={true}
                        onDrilldown={(filter) =>
                          openDrilldown(w, filter)
                        }
                      />
                    </div>
                  ))}
                </ResponsiveGridLayout>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Drilldown panel (same pattern as edit page) */}
      {drilldownWidget && (
        <div
          className="drilldown-backdrop"
          onClick={closeDrilldown}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="drilldown-panel"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "90%",
              height: "80%",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              transform: "perspective(1000px) translateZ(50px)",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            }}
          >
            <div
              className="drilldown-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "20px",
                borderBottom: "1px solid #e5e7eb",
                paddingBottom: "10px",
              }}
            >
              <div>
                <h3
                  className="drilldown-title"
                  style={{ margin: 0, fontSize: "1.5rem", fontWeight: "600" }}
                >
                  Drilldown – {drilldownWidget.title}
                </h3>
                <p
                  className="drilldown-subtitle"
                  style={{
                    margin: "5px 0 0 0",
                    color: "#6b7280",
                    fontSize: "0.9rem",
                  }}
                >
                  Dataset ID: {drilldownWidget.dataset_id}
                </p>
                {drilldownFilter && (
                  <p
                    className="drilldown-filter"
                    style={{
                      margin: "5px 0 0 0",
                      color: "#374151",
                      fontSize: "0.9rem",
                    }}
                  >
                    Filters:&nbsp;
                    {Object.entries(drilldownFilter)
                      .map(([k, v]) => `${k} = ${v}`)
                      .join(", ")}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="btn btn--icon"
                onClick={closeDrilldown}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  padding: "5px",
                  borderRadius: "4px",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "rgba(0,0,0,0.1)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "transparent")
                }
              >
                ✕
              </button>
            </div>

            {drilldownLoading ? (
              <SkeletonTable rows={5} columns={4} className="flex-1" />
            ) : drilldownError ? (
              <div
                className="mydash-message mydash-message--error"
                style={{
                  padding: "16px",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  color: "#dc2626",
                  margin: "20px 0",
                }}
              >
                {drilldownError}
              </div>
            ) : !drilldownData.length ? (
              <div
                className="drilldown-empty"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  color: "#6b7280",
                }}
              >
                <div
                  className="empty-icon"
                  style={{ fontSize: "3rem", marginBottom: "1rem" }}
                >
                  👀
                </div>
                <p>No data returned for this dataset.</p>
              </div>
            ) : (
              <>
                <div
                  className="drilldown-search-row"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: "#6b7280",
                    }}
                  >
                    Showing {filteredDrilldownData.length} of{" "}
                    {drilldownData.length} rows
                    {drilldownSearch ? " (filtered)" : ""}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search in results..."
                      value={drilldownSearch}
                      onChange={(e) =>
                        setDrilldownSearch(e.target.value)
                      }
                      style={{
                        padding: "6px 10px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        fontSize: "0.85rem",
                        minWidth: "220px",
                        outline: "none",
                      }}
                    />
                    {drilldownSearch && (
                      <button
                        type="button"
                        onClick={() => setDrilldownSearch("")}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "6px",
                          border: "1px solid #d1d5db",
                          backgroundColor: "#f9fafb",
                          fontSize: "0.8rem",
                          cursor: "pointer",
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                <div
                  className="drilldown-table-wrapper"
                  style={{
                    flex: 1,
                    overflow: "auto",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                >
                  {filteredDrilldownData.length === 0 ? (
                    <div
                      style={{
                        padding: "16px",
                        fontSize: "0.9rem",
                        color: "#6b7280",
                        textAlign: "center",
                      }}
                    >
                      No rows match your search.
                    </div>
                  ) : (
                    <table
                      className="drilldown-table"
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "14px",
                      }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: "#f9fafb" }}>
                          {drilldownHeaders.map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: "12px",
                                textAlign: "left",
                                borderBottom: "1px solid #e5e7eb",
                                fontWeight: "600",
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#f9fafb",
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDrilldownData.map((row, i) => (
                          <tr
                            key={i}
                            style={{
                              backgroundColor:
                                i % 2 === 0 ? "#ffffff" : "#f9fafb",
                            }}
                          >
                            {drilldownHeaders.map((h) => (
                              <td
                                key={h}
                                style={{
                                  padding: "12px",
                                  borderBottom:
                                    "1px solid #e5e7eb",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {String(row[h] ?? "")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}